// Slice 8: Investor Page Preview.
import { useEffect, useState } from "react";
import { Eye, MessagesSquare, ExternalLink, Building2 } from "lucide-react";
import { Link } from "wouter";
import { ChetanClipsGrid } from "@/components/chetan-clips-grid";
import { useCohort, fp } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

interface Section { id: number; field: string; fieldLabel: string; value: string; sourceLabel: string; status: string; }
interface PreviewData {
  company: { name: string; sector: string | null; foundedYear: number | null; website: string | null; };
  sections: Section[];
  latestUpdate: { weekOf: string; body: string | null; publishedAt: string | null } | null;
  isEmpty: boolean;
}

export default function FounderInvestorPreview() {
  const { slug } = useCohort();
  const [data, setData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [done, setDone] = useState<Set<number>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}api/founder-portal/investor-preview`, { headers: auth() });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, []);

  async function requestChange(sectionId: number, fieldLabel: string, currentValue: string) {
    const note = window.prompt(`Tell your Advisor what to change about "${fieldLabel}".`);
    if (!note || !note.trim()) return;
    setBusy(sectionId);
    try {
      await fetch(`${BASE}api/founder-portal/thread/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          body: `Change request for "${fieldLabel}" on the investor page.\n\nWhat is there now: ${currentValue}\n\nWhat I want changed: ${note.trim()}`,
        }),
      });
      setDone(s => new Set(s).add(sectionId));
    } finally { setBusy(null); }
  }

  if (loading) return <div className="p-12 text-white/60 text-sm">Loading.</div>;
  if (!data) return <div className="p-12 text-red-400 text-sm">Could not load preview.</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Investor Page Preview</div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">What an investor sees on your page</h1>
        <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
          This is a read-only mirror. To change anything, click "Request a change" next to that section. Your request goes to your Advisor.
        </p>
      </div>

      {data.isEmpty ? (
        <div className="bg-black/30 border border-white/[0.08] rounded-xl p-10 text-center">
          <Eye className="w-8 h-8 text-white/60 mx-auto mb-3" />
          <p className="text-sm text-white/65 mb-2">Your investor page is empty.</p>
          <p className="text-xs text-white/60 mb-4">It will fill up as you accept claims in Review Room or admin publishes them.</p>
          <Link href={fp(slug, "review")} className="text-xs text-[#FF5C00] hover:text-[#FF5C00]/80 font-medium">
            Open Review Room →
          </Link>
        </div>
      ) : (
        <>
          {/* Company header */}
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-6 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-white/60" />
              <span className="text-xs font-mono uppercase tracking-wider text-white/60">Company</span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight mb-1">{data.company.name}</h2>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/60">
              {data.company.sector && <span>{data.company.sector}</span>}
              {data.company.foundedYear && <span>· Founded {data.company.foundedYear}</span>}
              {data.company.website && (
                <a href={data.company.website} target="_blank" rel="noreferrer" className="text-[#FF5C00]/80 hover:text-[#FF5C00] inline-flex items-center gap-1">
                  {data.company.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-3 mb-6">
            {data.sections.map(s => (
              <div key={s.id} className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="text-xs font-mono uppercase tracking-wider text-white/60">{s.fieldLabel}</div>
                  {s.status === "admin_published" && (
                    <span className="text-[10px] font-mono uppercase tracking-wider bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded px-2 py-0.5">
                      Admin published
                    </span>
                  )}
                </div>
                <p className="text-sm text-white/85 leading-relaxed mb-3">{s.value}</p>
                {done.has(s.id) ? (
                  <div className="text-xs text-green-300">Sent to your Advisor.</div>
                ) : (
                  <button
                    onClick={() => requestChange(s.id, s.fieldLabel, s.value)}
                    disabled={busy === s.id}
                    data-testid={`button-request-change-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-xs text-[#FF5C00] hover:text-[#FF5C00]/80 font-medium"
                  >
                    <MessagesSquare className="w-3 h-3" />
                    {busy === s.id ? "Sending" : "Request a change"}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Latest update */}
          {data.latestUpdate && (
            <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-white/60 mb-3">Latest weekly update · week of {data.latestUpdate.weekOf}</div>
              {data.latestUpdate.body && <pre className="text-sm text-white/85 whitespace-pre-wrap font-sans leading-relaxed">{data.latestUpdate.body}</pre>}
            </div>
          )}

          {/* Chetan: clips on Shubh Labh */}
          <div className="mt-6">
            <ChetanClipsGrid endpoint="api/founder-portal/clips" tone="dark" />
          </div>
        </>
      )}
    </div>
  );
}
